import React from 'react';

interface SeatSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: () => void;
    seatData: {
        seatUuid: string;
        seatNumber: string;
        zone: string;
        row: string;
        seat: string;
    } | null;
    ticketData: {
        ticketId: string;
        ticketName: string;
        price: number;
        description?: string;
    } | null;
    eventData?: {
        title: string;
        date: string;
        doorsOpen: string;
        showStart: string;
        venue: string;
    };
    ticketColor?: { fill: string; stroke: string };
}

const SeatSelectionModal: React.FC<SeatSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelect,
    seatData,
    ticketData,
    eventData,
    ticketColor
}) => {
    // Use the passed ticket color or fallback to default green
    const modalColor = ticketColor?.fill || '#10b981';
    if (!isOpen || !seatData || !ticketData) {
        return null;
    }

    return (
        <div className="bg-white dark:bg-nightElevated rounded shadow-2xl max-w-sm w-full ml-0 mr-[1rem] overflow-hidden">
            {/* Seat Information Section */}
            <div className="p-0">
                <div className="grid grid-cols-3 gap-0 mb-0">
                    {/* Section */}
                    <div className="text-center border-r border-gray-100 dark:border-nightBorder p-3">
                        <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">SECTION</div>
                        <div className="text-2xl font-bold text-[#222] dark:text-nightTextSecondary uppercase">
                            {seatData.zone.length > 6 ? seatData.zone.split(' ').map(part => part.charAt(0)).join('') : seatData.zone}
                        </div>
                    </div>
                    {/* Row */}
                    <div className="text-center border-r border-gray-100 dark:border-nightBorder p-3">
                        <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">ROW</div>
                        <div className="text-2xl font-bold text-[#222] dark:text-nightTextSecondary">{seatData.row}</div>
                    </div>
                    {/* Seat */}
                    <div className="text-center p-3">
                        <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">SEAT</div>
                        <div className="text-2xl font-bold text-[#222] dark:text-nightTextSecondary">{seatData.seat}</div>
                    </div>
                </div>
            </div>

            {/* Price Section */}
            <div
                className="px-6 py-4 flex justify-between items-center"
                style={{ backgroundColor: modalColor }}
            >
                <div className="text-white font-bold text-lg">
                    {ticketData.ticketName}
                </div>
                <div className="text-white font-bold text-lg">
                    ${Number(ticketData.price || 0).toFixed(2)}
                </div>
            </div>

            {/* Event Details Section */}
            <div className="p-6">
                <div className="text-center mb-0">
                    <div className="text-lg font-bold text-[#222] dark:text-nightTextSecondary">
                        {seatData.zone} - {ticketData.ticketName}
                    </div>
                </div>

                {eventData && (
                    <div className="text-sm text-[#666] dark:text-nightMuted space-y-1">
                        <div className="font-medium text-[#222] dark:text-nightTextSecondary">{eventData.title}</div>
                        <div>Detail: {eventData.date}</div>
                        <div>Doors open: at {eventData.doorsOpen}</div>
                        <div>Show Start: at {eventData.showStart} Sharp</div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex">
                <button
                    onClick={onClose}
                    className="flex-1 bg-gray-100 dark:bg-white/10 text-[#222] dark:text-nightTextSecondary font-bold py-4 px-6 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                >
                    Close
                </button>
                <button
                    onClick={onSelect}
                    className="flex-1 bg-primary40 text-white font-bold py-4 px-6 hover:opacity-90 transition-opacity flex items-center justify-center gap-2 dark:bg-nightAccent dark:text-[#080808] rounded"
                >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Select
                </button>
            </div>
        </div>
    );
};

export default SeatSelectionModal;
